import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { DomainResolverService } from './domain-resolver.service';
import { WhitelabelConfigStore } from './whitelabel-config.store';
import { WhitelabelConfigResponse } from './whitelabel-config.model';
import { validateWhitelabelResponse } from './whitelabel-config.validator';
import { firstValueFrom, timeout } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class WhitelabelConfigService {
  private loadPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private domainResolver: DomainResolverService,
    private store: WhitelabelConfigStore
  ) {}

  load(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = this.loadOnce();
    }
    return this.loadPromise;
  }

  retry(): Promise<void> {
    this.loadPromise = null;
    return this.load();
  }

  private async loadOnce(): Promise<void> {
    // No configuration backend is deployed yet: always load the config
    // bundled with this build instead of calling environment.whitelabelConfigApi.
    const domain = this.domainResolver.resolve();
    const url = this.domainResolver.isLocal(domain)
      ? environment.localWhitelabelConfig
      : environment.runtimeConfigUrl;

    try {
      const response = await firstValueFrom(
        this.http.get<WhitelabelConfigResponse>(url).pipe(
          timeout({ first: environment.whitelabelConfigTimeoutMs })
        )
      );
      this.store.setReady(validateWhitelabelResponse(response, domain));
    } catch (error) {
      this.store.setError(this.toSafeLoadMessage(error));
    }
  }

  private toSafeMessage(error: unknown): string {
    return error instanceof Error
      ? error.message
      : 'This operator configuration is invalid.';
  }

  private toSafeLoadMessage(error: unknown): string {
    if (error && typeof error === 'object' && (error as { name?: string }).name === 'TimeoutError') {
      return 'The operator configuration took too long to load. Please try again.';
    }
    if (error instanceof Error && error.name !== 'HttpErrorResponse') {
      return this.toSafeMessage(error);
    }
    return 'We could not load this operator. Check the address or try again later.';
  }
}
